import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AuthState, User } from '../../types';
import { authService } from '../../services/authService';
import { api } from '../../services/api';
import { loginCacheStrategy } from '../../services/loginCacheStrategy';
import { shouldSkipOfflineCache } from '../../config/environments';

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await authService.login(email, password);

    // Save authentication data for offline access
    await AsyncStorage.setItem('token', response.token);
    if (response.user) {
      await AsyncStorage.setItem('user', JSON.stringify(response.user));
    }

    // IMPORTANT: Set the token in the API service BEFORE caching data
    await api.setToken(response.token);

    // Cache all data for offline use after successful login
    if (Platform.OS !== 'web' && !shouldSkipOfflineCache) {
      try {
        await loginCacheStrategy.cacheAllDataForOffline();
      } catch (error) {
        console.error('[Auth] Failed to cache data for offline use:', error);
        // Don't fail the login if caching fails
      }
    }

    return response;
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
  await api.setToken(null);

  // Clear cached authentication data
  await AsyncStorage.removeItem('token');
  await AsyncStorage.removeItem('user');

  // Clear cached offline data
  if (Platform.OS !== 'web' && !shouldSkipOfflineCache) {
    try {
      await loginCacheStrategy.clearAllCaches();
    } catch (error) {
      console.warn('[Auth] Error clearing cached data:', error);
    }
  }
});

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    if (!state.auth.token) throw new Error('No token available');
    
    const response = await authService.refreshToken(state.auth.token);
    await AsyncStorage.setItem('token', response.token);
    return response;
  }
);


const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
    authFailed: (state) => {
      // Clear auth state when authentication fails (e.g., 401 error)
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.error.message || 'Login failed';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
      });
  },
});

export const { setToken, setUser, clearAuth, authFailed } = authSlice.actions;
export default authSlice.reducer;
